import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FareOptionComponent, ClipLoaderCommon } from "@components";
import { breakpoint, useWindowDimensions } from "@utilities";
import { PATH_BOOK_NOW, PATH_HOME, PATH_LOGIN, PATH_QUOTES } from "@routes";
import {
    // IProductAvailOption,
    SelectedAvailability,
    IProductPricesDetails,
    IBookingAvailability,
    CheckProductAvailsData,
    CheckProductAvailsDateRange,
    ITourFareBookingTime,
} from "@interfaces";
import { AvailabilityRangePicker, CheckAvailTableModule } from "@module";
import {
    openToast,
    useAppSelector,
    useAppDispatch,
    addToBookNow,
    addToCartAndFetch,
    selectAuth,
    selectAccount,
} from "@redux";
import { DURATION_TIME } from "@constants";
import _ from "lodash";
import { LoadingStage } from "src/Interfaces/shared";
import { getProductPricesDetailsId, getBookingAvailabilities } from "src/Utilities/cartHelper";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { AddToCartButton } from "src/Module/TourCheckAvail/AddToCartButton";
import { addToExistingQuoteModal, createNewQuoteModal } from "src/Module/Modals";
import {
    addItemToExistingQuoteAndReload,
    createNewQuoteAndReload,
    loadQuotes,
    selectQuotes,
} from "src/Redux/Slices/quotes";
import { IQuote } from "src/Interfaces/quotes";
import moment from "moment";
import { Provider as ReactSpectrumProvider, defaultTheme } from "@adobe/react-spectrum";
import { SuccessToast } from "src/Components/toasts/SuccessToast";

export const CHANGE_DATE_FROM = "FROM";
export const CHANGE_DATE_TO = "TO";

interface CheckAvailLimitDate {
    from: Date;
    to: Date;
}

interface CheckAvailsProps extends CheckProductAvailsData {
    productClass: string;
    limitDate: CheckAvailLimitDate;
    selectedAvailabilities: SelectedAvailability[];
    setSelectedAvailabilities: (newAvailabilities: SelectedAvailability[]) => void;
}

export const ProductAvailabilities = ({
    startDate,
    numDays,
    selectedBookingTimeId,
    productId,
    bookingtPriceGroup: productPriceGroup,
    apiProviderId,
    groupFaresForAvailabilityCheck,
    selectedAvailabilities,
    setSelectedAvailabilities,
}: CheckAvailsProps) => {
    const dispatch = useAppDispatch();
    const [availabilities, setAvailabilities] = useState<IBookingAvailability[]>([]);
    const [, setAvailLoadingStage] = useState<LoadingStage>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { width } = useWindowDimensions();

    useEffect(() => {
        if (numDays >= 0) {
            setIsLoading(true);
            getBookingAvailabilities({
                startDate,
                numDays,
                selectedBookingTimeId,
                productId,
                bookingtPriceGroup: productPriceGroup,
                apiProviderId,
                groupFaresForAvailabilityCheck,
            })
                .then((avails) => {
                    setAvailabilities(avails);
                    setAvailLoadingStage(LoadingStage.INITIALIZE_COMPLETE);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error(error);
                    setAvailLoadingStage(LoadingStage.LOADING_FAILURE);
                    setIsLoading(false);
                });
        } else {
            dispatch(
                openToast({
                    type: "error",
                    autoHideDuration: DURATION_TIME,
                    message: "Invalid date.",
                }),
            );
        }
    }, [startDate, numDays]);

    const handleAvailCheck = (checkedAvail: SelectedAvailability) => {
        let updatedCheckedDate = [];
        const checkedIndex = selectedAvailabilities.findIndex(
            (date) => checkedAvail.BookingDate === date.BookingDate,
        );

        if (checkedIndex >= 0) {
            updatedCheckedDate = selectedAvailabilities.filter(
                (date) => date.BookingDate !== checkedAvail.BookingDate,
            );
        } else {
            updatedCheckedDate = [...selectedAvailabilities, checkedAvail];
        }

        setSelectedAvailabilities(updatedCheckedDate);
    };

    return (
        <Container>
            {isLoading ? (
                <div className="clip-loader">
                    <ClipLoaderCommon color="#31B4B9" size={70} margin={10} />
                </div>
            ) : availabilities.length < 1 ? (
                <p style={{ textAlign: "center" }}>
                    There is no availability for this fare. Please change your date or select
                    another fare.
                </p>
            ) : width < 767 ? (
                <>
                    {availabilities.map((availability, index) => {
                        const isChecked = selectedAvailabilities.some(
                            (dateItem) => dateItem.BookingDate === availability.BookingDate,
                        );
                        return (
                            <FareOptionComponent
                                key={`avail-${index}`}
                                availIndex={index}
                                isChecked={isChecked}
                                availability={availability}
                                onCheck={handleAvailCheck}
                            />
                        );
                    })}
                </>
            ) : (
                <CheckAvailTableModule
                    availabilities={availabilities}
                    selectedAvailabilities={selectedAvailabilities}
                    setSelectedAvailabilities={setSelectedAvailabilities}
                    isLoading={isLoading}
                    range={{
                        startDate,
                        numDays,
                    }}
                />
            )}
        </Container>
    );
};

const Container = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const ModalFooter = styled.div`
    position: sticky;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    padding: 16px;
    border-top: 1px solid #e0e0e0;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
`;

interface CheckAvailsModalProps extends CheckProductAvailsData {
    productClass: string;
    limitDate: CheckAvailLimitDate;
    isOpen: boolean;
    onClose: () => void;
    bookingDetails: ITourFareBookingTime;
}

export const CheckAvailsModal = (props: CheckAvailsModalProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const addToCartRef = useRef<HTMLDivElement | null>(null);
    const { jwt } = useAppSelector(selectAuth);
    const { account } = useAppSelector(selectAccount);
    const { quotes } = useAppSelector(selectQuotes);
    const shouldRedirect = useMemo(() => _.isNil(account) || _.isNil(jwt), [account, jwt]);
    const [_searchParams, setSearchParams] = useSearchParams();
    const { isOpen, onClose, bookingtPriceGroup: productPriceGroup, startDate, numDays } = props;

    const handleClose = () => {
        const currentParams = Object.fromEntries(_searchParams);
        delete currentParams.startDate;
        delete currentParams.days;
        delete currentParams.bookingTimeId;
        setSearchParams(currentParams);
        onClose();
    };

    if (!isOpen) return null;
    // const firstFare = _.first(productPriceGroup.prices);
    const [selectedAvailabilities, setSelectedAvailabilities] = useState<SelectedAvailability[]>(
        [],
    );
    const maxQuantity = _.min(_.map(selectedAvailabilities, (item) => item.NumAvailable)) || 1;
    // Determine the travel end date from `firstFare` if it exists
    // const travelEnd = !_.isNil(firstFare?.travelEnd)
    //     ? moment(firstFare!.travelEnd!, FARE_PRICE_TRAVEL_DATE_FORMAT).toDate()
    //     : null;

    // Set a default "to" date for availability checking
    // If `travelEnd` exists and is within 14 days from today, use `travelEnd`
    // Otherwise, set `to` date as 14 days from today
    // const defaultToDate =
    //     travelEnd && moment(travelEnd).isBefore(moment().add(14, "days"))
    //         ? travelEnd
    //         : moment(now).add(14, "days").toDate();

    // Define state to manage the date range for availability checking
    // const [checkAvailDateRange, setDate] = useState<Booking.CheckProductAvailsDateRange>({
    //     startDate,
    //     numDays,
    // });
    const [dateRange, setDateRange] = useState<CheckProductAvailsDateRange>({
        startDate: startDate,
        numDays: Math.abs(numDays),
    });
    const [isSubmitInProgress, setIsSubmitInProgress] = useState<
        null | "cart" | "book-now" | "quote"
    >(null);
    const [quantity, setQuantity] = useState<number>(1);
    const increaseQuantity = () => {
        const allAvailabilitiesAllowIncrease = selectedAvailabilities.every(
            (avail) => avail.NumAvailable >= quantity + 1,
        );

        if (!allAvailabilitiesAllowIncrease) {
            dispatch(
                openToast({
                    type: "warning",
                    message: "Max allowed quantity reached",
                    autoHideDuration: DURATION_TIME,
                }),
            );
            return;
        }

        setQuantity((prev) => (prev < maxQuantity ? prev + 1 : prev));
    };

    useEffect(() => {
        setSearchParams({
            startDate: dateRange.startDate.toISOString(),
            days: dateRange.numDays.toString(),
            bookingTimeId: props.selectedBookingTimeId,
        });
    }, [dateRange.startDate, dateRange.numDays]);

    const decreaseQuantity = () => {
        if (quantity > 1) {
            const nextQuantity = quantity - 1;
            setQuantity(nextQuantity);
        } else {
            ErrorToast({
                Message: "Minimum quantity reached",
            });
        }
    };

    // useEffect(() => {
    //     document.body.style.overflow = "hidden";
    //     return () => {
    //         document.body.style.overflow = "";
    //     };
    // }, []);
    const handleAdd = async (
        selectedAvailabilities: SelectedAvailability[],
        mode: "addToCart" | "bookNow" | "newQuote" | "existingQuote",
        // TODO: remove or use
        // timeId?: IProductAvailOption,
        // pickUpLocation?: IProductAvailOption,
        // dropOffLocation?: IProductAvailOption,
    ) => {
        if (shouldRedirect) {
            const returnPath = encodeURIComponent(location.pathname);
            const params = `?return_to=${returnPath}`;
            navigate(`${PATH_LOGIN}${params}`, { replace: true });
            return;
        }
        const actionType =
            mode === "addToCart" ? "cart" : mode === "bookNow" ? "book-now" : "quote";
        try {
            setIsSubmitInProgress(actionType);
            const selectedAvailableIndices = selectedAvailabilities.map((sa) => sa.availIndex);
            const productPricesDetails: IProductPricesDetails[] = _.chain(selectedAvailabilities)
                .groupBy((sa) => getProductPricesDetailsId(sa))
                .map((sas, ppdid) => ({
                    productPricesDetailsId: ppdid,
                    quantityDetails: sas.map((sa) => ({
                        quantity,
                        bookingDate: sa.BookingDate,
                        timeId: props.selectedBookingTimeId,
                        // no need to pass booking data when adding to cart
                        // TODO: store commences and booking data
                    })),
                }))
                .value();
            const addItemData = {
                tdmsProductId: props.productId,
                productPricesDetails,
                startDate: moment(dateRange.startDate).format("DD-MMM-YYYY"),
                days: dateRange.numDays,
                selectedAvailableIndices,
            };
            if (mode === "newQuote") {
                await createNewQuoteModal().then(async (title?: string) => {
                    if (!_.isEmpty(title)) {
                        await dispatch(
                            createNewQuoteAndReload({
                                ...addItemData,
                                quoteTitle: title!,
                            }),
                        ).unwrap();
                        navigate(PATH_QUOTES);
                    }
                });
            } else if (mode === "existingQuote") {
                await dispatch(loadQuotes());
                await addToExistingQuoteModal({ quotes }).then(async (quote?: IQuote) => {
                    if (!_.isNil(quote)) {
                        await dispatch(
                            addItemToExistingQuoteAndReload({
                                ...addItemData,
                                quoteId: quote.id.toString(),
                            }),
                        ).unwrap();
                        navigate(PATH_QUOTES);
                    }
                });
            } else if (mode === "bookNow") {
                await dispatch(addToBookNow(addItemData)).unwrap();
                navigate(PATH_BOOK_NOW);
            } else if (mode === "addToCart") {
                await dispatch(addToCartAndFetch(addItemData)).unwrap();
                navigate(PATH_HOME, {
                    state: { showRedirectToCartDialog: true },
                });
                SuccessToast({
                    Message: "Added to cart successfully",
                });
            }
        } catch (error: any) {
            ErrorToast({ Message: error.title });
        } finally {
            setIsSubmitInProgress(null);
        }
    };

    return (
        <>
            <ReactSpectrumProvider theme={defaultTheme}>
                <ModalOverlay onClick={handleClose}>
                    <ModalContainer onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <CloseButton onClick={handleClose}>
                                <img src="/images/fi_x.png" alt="close" />
                            </CloseButton>
                            <div className="price-group-info">
                                <h2 className="text">{productPriceGroup.name}</h2>
                                {productPriceGroup.travelTimeMessage && (
                                    <div className="travel-time">
                                        {productPriceGroup.travelTimeMessage}
                                    </div>
                                )}
                            </div>
                            <DateField>
                                <AvailabilityRangePicker
                                    range={dateRange}
                                    limitDate={props.limitDate}
                                    onRangeChange={(startDate, numDays) => {
                                        setDateRange({
                                            startDate,
                                            numDays,
                                        });
                                    }}
                                />
                            </DateField>
                        </ModalHeader>
                        <ModalBody>
                            <ProductAvailabilities
                                {...props}
                                startDate={dateRange.startDate}
                                numDays={dateRange.numDays}
                                selectedAvailabilities={selectedAvailabilities}
                                setSelectedAvailabilities={setSelectedAvailabilities}
                            />
                        </ModalBody>
                        {selectedAvailabilities.length > 0 && (
                            <ModalFooter>
                                <AddToCartButton
                                    ref={addToCartRef}
                                    productClass={props.productClass}
                                    isSubmitInProgress={isSubmitInProgress}
                                    selectedAvailabilities={selectedAvailabilities}
                                    selectedBookingTimeId={props.selectedBookingTimeId}
                                    bookingDetails={props.bookingDetails}
                                    bookingSystem={props.bookingtPriceGroup.bookingSystem}
                                    handleAdd={handleAdd}
                                    increaseQuantity={increaseQuantity}
                                    decreaseQuantity={decreaseQuantity}
                                    quantity={quantity}
                                />
                            </ModalFooter>
                        )}
                    </ModalContainer>
                </ModalOverlay>
            </ReactSpectrumProvider>
        </>
    );
};

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    -webkit-box-align: center;
    -moz-box-align: center;

    @media (max-width: 767px) {
        padding-top: 0;
        align-items: flex-start;
        background-color: #fff;
        overflow-y: auto;
    }

    color: #333;
    font-family: "DM Sans", sans-serif;
    font-size: 15px;
`;

const ModalContainer = styled.div`
    background: #fff;
    border-radius: 8px;
    width: 95%;
    max-width: 1200px;
    max-height: 70vh;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    @media (max-width: 767px) {
        padding-top: 80px;
        width: 100%;
        height: 100vh;
        max-height: 100%;
        border-radius: 0;
        overflow-y: auto;
    }
`;

const ModalHeader = styled.div`
    position: relative;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    z-index: 1000;

    .price-group-info {
        flex: 1;
        margin-right: 20px;
        text-align: left;

        .text {
            margin: 0;
        }

        .travel-time {
            margin-top: 8px;
        }
    }

    @media (max-width: 767px) {
        flex-direction: column;
        background-color: #f8f8f8;

        .price-group-info {
            width: 100%;
            margin-right: 0;
            margin-bottom: 16px;
        }
    }
`;

const DateField = styled.div`
    display: flex;
    gap: 20px;
    flex-shrink: 0;
    margin-right: 20px;

    @media (max-width: 767px) {
        margin-right: 0px;
    }

    .datePicker {
        .MuiInputLabel-animated {
            color: #fff;
        }

        .MuiFormControl-marginNormal {
            margin: 0;
        }

        .MuiInputBase-root {
            background-color: ${(p) => p.theme.colors.gray_1} !important;
            height: 36px;
            width: 135px;

            ${breakpoint.breakIpadPro`
        width: 160px;
      `}

            ${breakpoint.breakLarge`
        width: 214px;
      `}
        }

        .MuiInputBase-input {
            color: #fff;
        }
    }
`;

const ModalBody = styled.div`
    padding: 20px;
    overflow-y: auto;
    flex: 1;
`;
const CloseButton = styled.button`
    position: absolute;
    right: 14px;
    top: 22px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    &:hover {
        color: #000;
    }
    z-index: 1001;
`;
